import { useInfiniteQuery } from '@tanstack/react-query';

import { client } from '@/api/common';
import type { Reservation } from '@/api/reservations';

type Variables = {};
type Options = {
  enabled?: boolean;
};

type ReservationsPage = {
  data: Reservation[];
  meta: {
    page: number;
    totalPages: number;
    totalCount: number;
  };
};

// Utility to check if required params are defined
const areRequiredParamsDefined = (
  variables: Variables,
  requiredKeys: (keyof Variables)[]
) => requiredKeys.every((key) => variables[key] !== '');

const fetchReservations = async ({
  pageParam,
  variables: _variables,
}: {
  pageParam: number;
  variables: Variables;
}) => {
  try {
    let params: Record<string, any> = {
      page: pageParam,
      per_page: 5,
    };

    const response = await client.get(
      'v2/internal/partners/reservations/to-review',
      {
        params,
      }
    );

    return response.data;
  } catch (error) {
    console.error('fetchReservations error:', error);
    throw error;
  }
};

export const useToReviewReservations = (
  variables: Variables,
  options?: Options
) => {
  const requiredKeys: (keyof Variables)[] = [];

  const shouldFetch = areRequiredParamsDefined(variables, requiredKeys);

  return useInfiniteQuery({
    queryKey: ['internal/partners/reservations/to-review'],
    queryFn: ({ pageParam = 1 }) =>
      fetchReservations({
        pageParam,
        variables,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ReservationsPage) => {
      if (lastPage.meta.page >= lastPage.meta.totalPages) {
        return undefined;
      }
      return lastPage.meta.page + 1;
    },
    enabled: shouldFetch && (options?.enabled ?? true),
  });
};
